import type { ConnectedPosition } from '@angular/cdk/overlay';
import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    inject,
    input,
    output,
    signal,
    viewChild,
    type OnChanges,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { NgIcon } from '@ng-icons/core';
import {
    BuildingNamePipe,
    QpxButton,
    QpxIcon,
    injectPopover,
    type PopoverRef,
} from '@quipex/shared/ui';
import type { BuildingScopeSelection, ScopePickerResult } from './models';
import type { ScopePickerPopover } from './scope-picker-popover/scope-picker-popover';

const SCOPE_PICKER_POSITIONS: ConnectedPosition[] = [
    {
        originX: 'start',
        originY: 'bottom',
        overlayX: 'start',
        overlayY: 'top',
        offsetY: 8,
    },
    {
        originX: 'start',
        originY: 'top',
        overlayX: 'start',
        overlayY: 'bottom',
        offsetY: -8,
    },
];

@Component({
    selector: 'qpx-building-scope-picker',
    imports: [MatButtonModule, NgIcon, BuildingNamePipe, QpxButton, QpxIcon],
    templateUrl: './building-scope-picker.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'inline-block min-w-0' },
})
export class BuildingScopePicker implements OnChanges {
    private readonly popover = injectPopover();
    private readonly destroyRef = inject(DestroyRef);
    private readonly trigger = viewChild.required<
        unknown,
        ElementRef<HTMLButtonElement>
    >('trigger', { read: ElementRef });
    private ref: PopoverRef<ScopePickerResult> | undefined;
    private scopeKey: string | undefined;

    public readonly selection = input.required<BuildingScopeSelection>();
    public readonly selectionChange = output<BuildingScopeSelection>();
    protected readonly opened = signal(false);

    public constructor() {
        this.destroyRef.onDestroy(() => this.ref?.close());
    }

    public ngOnChanges(): void {
        const selection = this.selection();
        const key =
            selection.kind === 'portfolio'
                ? 'portfolio'
                : selection.building.id;
        if (this.scopeKey !== key) this.ref?.close();
        this.scopeKey = key;
    }

    protected open(): void {
        const origin = this.trigger().nativeElement;
        if (this.opened()) {
            this.ref?.close();
            return;
        }
        this.opened.set(true);
        this.ref = this.popover.open<ScopePickerPopover, ScopePickerResult>({
            loadComponent: () =>
                import('./scope-picker-popover/scope-picker-popover').then(
                    (module) => module.ScopePickerPopover
                ),
            origin,
            componentInputs: { selection: this.selection },
            closeOnOutsidePointer: true,
            positions: SCOPE_PICKER_POSITIONS,
        });
        this.ref
            .afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
                this.opened.set(false);
                if (
                    origin.isConnected &&
                    (result || document.activeElement === document.body)
                )
                    origin.focus();
                const confirmed = typeof result === 'object' ? result : null;
                if (confirmed?.confirm)
                    this.selectionChange.emit(confirmed.data);
            });
    }
}
